import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Check, Search, FolderPlus, FolderGit2, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const MODELS = [
  { key: "chatgpt", name: "ChatGPT" },
  { key: "claude", name: "Claude" },
  { key: "gemini", name: "Gemini" },
  { key: "deepseek", name: "DeepSeek" },
  { key: "perplexity", name: "Perplexity" },
  { key: "grok", name: "Grok" },
];

/**
 * New Project Chat — a Pro+ group chat with a linked project workspace
 * (tasks + files + project memory). Distinct from a plain New Group Chat.
 */
export default function NewProjectChatDialog({ open, onOpenChange, onCreated, onRequireUpgrade }) {
  const { user } = useAuth();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [members, setMembers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [query, setQuery] = useState("");
  const [defaultModels, setDefaultModels] = useState(["chatgpt", "claude", "gemini"]);
  const [postingPolicy, setPostingPolicy] = useState("all");
  const [workspaceMode, setWorkspaceMode] = useState("new"); // "new" | "link"
  const [folders, setFolders] = useState([]);
  const [folderId, setFolderId] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/workspace/members").then(({ data }) => setMembers(data || [])).catch(() => {});
    api.get("/folders").then(({ data }) => setFolders(data || [])).catch(() => {});
    setName(""); setDescription(""); setSelected([]); setQuery("");
    setDefaultModels(["chatgpt", "claude", "gemini"]);
    setPostingPolicy("all"); setWorkspaceMode("new"); setFolderId("");
  }, [open]);

  const toggleModel = (k) =>
    setDefaultModels((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k]));
  const toggleMember = (id) =>
    setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const filteredMembers = members.filter(
    (m) => m.id !== user?.id && (m.name || m.email || "").toLowerCase().includes(query.toLowerCase()),
  );

  const submit = async () => {
    if (!name.trim()) return toast.error("Project name required");
    if (workspaceMode === "link" && !folderId) return toast.error("Choose a project to link");
    setSubmitting(true);
    try {
      const body = {
        type: "group",
        kind: "project",
        name: name.trim(),
        description: description.trim(),
        member_ids: selected,
        default_models: defaultModels,
        posting_policy: postingPolicy,
        posting_user_ids: postingPolicy === "selected" ? [user?.id] : [],
      };
      if (workspaceMode === "link") body.project_folder_id = folderId;
      const { data } = await api.post("/chats", body);
      toast.success("Project Chat created");
      onCreated?.(data);
      onOpenChange(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === "project_chat_requires_pro") {
        onOpenChange(false);
        onRequireUpgrade?.();
        return;
      }
      toast.error(detail?.message || detail || "Could not create Project Chat");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a0a0a] border-white/10 rounded-sm max-w-lg max-h-[90vh] flex flex-col overflow-hidden p-0 gap-0" data-testid="new-project-chat-dialog">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-white/10 shrink-0 text-left">
          <DialogTitle className="font-display tracking-tight flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-brand" /> New Project Chat
          </DialogTitle>
          <p className="text-[12px] text-zinc-500 mt-1">Group chat + tasks + project workspace</p>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4" data-testid="new-project-scroll">
          <div>
            <div className="label-mono mb-2">PROJECT NAME</div>
            <Input data-testid="new-project-name" value={name} onChange={(e) => setName(e.target.value)} className="bg-[#121214] border-white/10 rounded-sm" placeholder="Project Atlas" />
          </div>
          <div>
            <div className="label-mono mb-2">DESCRIPTION</div>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} className="bg-[#121214] border-white/10 rounded-sm min-h-[56px]" placeholder="What is this project about?" />
          </div>

          <div>
            <div className="label-mono mb-2">PROJECT WORKSPACE</div>
            <div className="flex gap-2 mb-2">
              <button
                type="button"
                data-testid="project-mode-new"
                onClick={() => setWorkspaceMode("new")}
                className={`flex-1 px-3 py-2 rounded-sm border text-[12px] inline-flex items-center justify-center gap-1.5 ${workspaceMode === "new" ? "bg-brand text-black border-brand" : "border-white/10 text-zinc-400"}`}
              >
                <FolderPlus className="w-3.5 h-3.5" /> Create new project
              </button>
              <button
                type="button"
                data-testid="project-mode-link"
                onClick={() => setWorkspaceMode("link")}
                className={`flex-1 px-3 py-2 rounded-sm border text-[12px] inline-flex items-center justify-center gap-1.5 ${workspaceMode === "link" ? "bg-brand text-black border-brand" : "border-white/10 text-zinc-400"}`}
              >
                <FolderGit2 className="w-3.5 h-3.5" /> Link existing
              </button>
            </div>
            {workspaceMode === "link" && (
              <Select value={folderId || "__none__"} onValueChange={(v) => setFolderId(v === "__none__" ? "" : v)}>
                <SelectTrigger data-testid="project-folder-trigger" className="bg-[#121214] border-white/10 rounded-sm">
                  <SelectValue placeholder="Choose a project" />
                </SelectTrigger>
                <SelectContent className="bg-[#121214] border-white/10">
                  <SelectItem value="__none__">Choose a project</SelectItem>
                  {folders.map((f) => (
                    <SelectItem key={f.id} value={f.id} data-testid={`project-folder-option-${f.id}`}>{f.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div>
            <div className="label-mono mb-2">MEMBERS</div>
            <div className="relative mb-2">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search teammates" className="bg-[#121214] border-white/10 rounded-sm pl-8 h-9" />
            </div>
            <div className="max-h-40 overflow-y-auto rounded-sm border border-white/5 divide-y divide-white/5">
              {filteredMembers.length === 0 && (
                <div className="text-[12px] text-zinc-500 px-3 py-3">No teammates found.</div>
              )}
              {filteredMembers.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  data-testid={`project-member-${m.id}`}
                  onClick={() => toggleMember(m.id)}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-white/5"
                >
                  <span className={`w-4 h-4 rounded-sm border flex items-center justify-center ${selected.includes(m.id) ? "bg-brand border-brand" : "border-white/20"}`}>
                    {selected.includes(m.id) && <Check className="w-3 h-3 text-black" />}
                  </span>
                  <span className="text-[13px] text-zinc-200 flex-1 truncate">{m.name || m.email}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="label-mono mb-2">DEFAULT AI MODELS</div>
            <div className="flex flex-wrap gap-2">
              {MODELS.map((m) => (
                <button key={m.key} type="button" onClick={() => toggleModel(m.key)} className={`px-2 py-1 text-[10px] font-mono uppercase tracking-widest rounded-sm border ${defaultModels.includes(m.key) ? "bg-yellow-500 text-black border-yellow-500" : "border-white/10 text-zinc-400"}`}>
                  {m.name}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="label-mono mb-2">WHO CAN POST</div>
            <Select value={postingPolicy} onValueChange={setPostingPolicy}>
              <SelectTrigger data-testid="project-posting-trigger" className="bg-[#121214] border-white/10 rounded-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#121214] border-white/10">
                <SelectItem value="all">Everyone</SelectItem>
                <SelectItem value="selected">Only me &amp; admins</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="px-5 py-4 border-t border-white/10 shrink-0">
          <Button
            data-testid="new-project-submit"
            onClick={submit}
            disabled={submitting || !name.trim()}
            className="bg-brand text-black hover:bg-yellow-300 rounded-sm w-full h-11 font-semibold"
          >
            Create Project Chat
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
